import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { primaryButtonClass } from '@/components/common/formStyles'
import { useConfirmStrategy, useCurrentStrategy, useSeedStrategy } from '@/lib/useStrategy'
import { useOnboardingStore } from '@/stores/onboarding'

function isAxiosNotFound(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    'response' in err &&
    (err as { response?: { status?: number } }).response?.status === 404
  )
}

/**
 * Step 6: Default-Strategy seeden + bestätigen.
 *
 * Drei Zustände:
 * 1. Keine Strategy aktiv und kein Draft seedet → "v1.0 anlegen"-Button
 * 2. Draft existiert (über seed.data) → "v1.0 aktivieren"-Button
 * 3. Strategy aktiv → Erfolgs-Meldung + "Wizard abschließen"-Button
 */
export function Step6Strategy() {
  const navigate = useNavigate()
  const current = useCurrentStrategy()
  const seed = useSeedStrategy()
  const confirm = useConfirmStrategy()
  const markCompleted = useOnboardingStore((s) => s.markCompleted)
  const [seededDraftId, setSeededDraftId] = useState<number | null>(null)
  const [seededDraftVersion, setSeededDraftVersion] = useState<string | null>(null)

  // Bei aktivierter Strategy diesen Step als completed markieren — auch
  // wenn der User erst nach Auto-Redirect hier landet.
  useEffect(() => {
    if (current.data) {
      markCompleted(6)
    }
  }, [current.data, markCompleted])

  const noActive = current.isError && isAxiosNotFound(current.error)

  const handleSeed = async () => {
    const result = await seed.mutateAsync()
    setSeededDraftId(result.id)
    setSeededDraftVersion(result.version)
  }

  const handleConfirm = async () => {
    if (seededDraftId === null) return
    await confirm.mutateAsync(seededDraftId)
    markCompleted(6)
    navigate('/dashboard')
  }

  if (current.isLoading) {
    return <p className="font-mono text-sm text-muted">Lade Strategy …</p>
  }

  // Bereits aktive Strategy — User hat den Wizard erneut aufgerufen
  if (current.data) {
    const strategy = current.data
    return (
      <div className="flex flex-col gap-6">
        <div>
          <h2 className="tracked-caps text-sm text-ink">Strategie aktiv</h2>
          <p className="mt-3 max-w-2xl font-mono text-sm leading-6 text-muted">
            Strategy v{strategy.version} ist aktiv mit {strategy.rules.length} Regeln. Stop-Loss-,
            Profit-Taking- und Rebalancing-Checks laufen gegen dieses Regelwerk. Anpassungen via
            Fork in{' '}
            <Link to="/strategy" className="text-bernstein hover:underline">
              /strategy
            </Link>
            .
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            markCompleted(6)
            navigate('/dashboard')
          }}
          className={primaryButtonClass}
        >
          Zum Dashboard →
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="tracked-caps text-sm text-ink">Strategie bestätigen</h2>
        <p className="mt-3 max-w-2xl font-mono text-sm leading-6 text-muted">
          Sextant legt eine Default-Strategy v1.0 nach SEXTANT.md §3 an: Allokations-Ziele,
          Cluster-Regeln, Stop-Loss-Definitionen, verbotene Aktionen, Rebalancing- und
          Steuer-Regeln. Du kannst die Strategy jederzeit später via Fork ändern.
        </p>
      </div>

      {!seededDraftId && noActive && (
        <section className="hairline-y py-5">
          <p className="tracked-caps mb-3 text-[10px] text-faint">Schritt 1 von 2</p>
          <p className="mb-4 font-mono text-sm text-muted">
            Erst die Default-Regeln als Draft v1.0 anlegen, danach mit einem Klick aktivieren.
          </p>
          <button
            type="button"
            onClick={handleSeed}
            disabled={seed.isPending}
            className={primaryButtonClass}
          >
            {seed.isPending ? 'Erzeuge …' : 'v1.0 als Draft anlegen'}
          </button>
          {seed.isError && (
            <p role="alert" className="mt-3 font-mono text-xs text-loss">
              {(seed.error as Error).message}
            </p>
          )}
        </section>
      )}

      {seededDraftId && seededDraftVersion && (
        <section className="hairline-y py-5">
          <p className="tracked-caps mb-3 text-[10px] text-bernstein">Schritt 2 von 2</p>
          <p className="mb-4 font-mono text-sm text-ink">
            Draft v{seededDraftVersion} angelegt. Aktiviere sie, um Sextant einsatzbereit zu machen.
          </p>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={confirm.isPending}
            className={primaryButtonClass}
          >
            {confirm.isPending ? 'Aktiviere …' : `v${seededDraftVersion} aktivieren`}
          </button>
          {confirm.isError && (
            <p role="alert" className="mt-3 font-mono text-xs text-loss">
              {(confirm.error as Error).message}
            </p>
          )}
        </section>
      )}

      {current.isError && !noActive && (
        <p role="alert" className="font-mono text-xs text-loss">
          Strategy konnte nicht geladen werden: {(current.error as Error).message}
        </p>
      )}
    </div>
  )
}
